import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { formatNumber, Site } from '@debbie/common/dist';
import { objectBy } from '@debbie/common/dist/util';
import { TodoType } from '@debbie/common/dist/workflows';
import { c, h } from '@debbie/cortex/dist';
import { useConnectConnectionsFrom } from '@debbie/cortex/dist/connect';
import { selectTemplatesMap } from '@debbie/cortex/dist/templates/selectors';
import { selectWorkflows } from '@debbie/cortex/dist/workflows/selectors';
import THEME from '../../theme';
import { toLink } from '../../utils';
import DraggableList from '../DraggableList';
import { Edit, File, Info } from '../Icons';
import ExternalLink from '../Icons/ExternalLink';
import Trash2 from '../Icons/Trash2';
import { useEndReasons } from '@debbie/cortex/dist/end-reasons';
import Modal from '../Modal';
import { TemplateEditorTabSet } from '../templates';
import { useCaseGroups } from '@debbie/cortex/dist/caseGroups';
import Button from '../Button';
import Checkbox from '../Checkbox';
import { Size } from '../../design-system';
import { selectUpdateTypesMap } from '@debbie/cortex/dist/update-types/selectors';
import { useCollector } from '@debbie/cortex/dist/collectors';
const Procedure = (props) => {
    const [editableProcedure, setEditableProcedure] = useState(props.procedure);
    useEffect(() => {
        setEditableProcedure(props.procedure);
    }, [props.procedure]);
    if (!editableProcedure)
        return null;
    return (React.createElement(ProcedureBuilderStyled, null, props.onChange ? (React.createElement(DraggableList, { list: editableProcedure.map((todo, i) => ({
            id: todo.id,
            content: (React.createElement(TodoComponent, { i: i, todo: todo, onChange: props.onChange, procedure: props.procedure })),
        })), onChange: (items) => {
            if (props.onChange) {
                const newProcedure = items.map((item) => {
                    const todo = editableProcedure.find((todo) => todo.id === item.id);
                    if (!todo)
                        throw new Error('todo not found in procedure onChange');
                    return todo;
                });
                setEditableProcedure(newProcedure);
                props.onChange(newProcedure);
            }
        } })) : (editableProcedure.map((todo, i) => (React.createElement(TodoComponent, { key: todo.id, i: i, todo: todo, onChange: props.onChange, procedure: props.procedure }))))));
};
export default Procedure;
const ProcedureBuilderStyled = styled.div `
  text-align: left;

  & > h3 {
    font-size: 14px;
    margin-top: 0.5em;
  }
`;
const TodoNumber = styled.span `
  background-color: ${THEME.COLOR_ACCENT};
  color: ${THEME.COLOR_SECONDARY};
  width: 10px;
  height: 10px;
  padding: 2px 8px;
  border-radius: 3px;
  margin-right: 5px;
`;
const TodoStyled = styled.div `
  font-size: 12px;
  background: #e8e8e8;
  padding: 5px 10px 5px 3px;
  border-radius: 3px;
  & > svg {
    float: right;
  }

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
const TodoComponent = (props) => {
    const { todo, i } = props;
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const templatesMap = useSelector(selectTemplatesMap);
    const collectorLoadable = useCollector(todo.type === TodoType.SetCollector ? todo.details.collectorId : undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const caseGroupsLoadable = useCaseGroups();
    const caseGroups = h.hasData(caseGroupsLoadable)
        ? caseGroupsLoadable.data
        : null;
    const workflows = useSelector(selectWorkflows);
    const endReasons = useEndReasons();
    const endReasonLookup = h.hasData(endReasons)
        ? objectBy(endReasons.data, (er) => er.id)
        : {};
    const fromConnections = useConnectConnectionsFrom();
    const connectionLookup = objectBy(h.hasData(fromConnections) ? fromConnections.data : [], (c) => c.id);
    const workflowsLookup = objectBy(workflows ? workflows : [], (workflow) => workflow.workflowId);
    const updateTypesMap = useSelector(selectUpdateTypesMap);
    const [editTemplateId, setEditTemplateId] = useState(null);
    const [showTodoDetails, setShowTodoDetails] = useState(false);
    return (React.createElement("div", { style: { paddingTop: '5px' } },
        React.createElement(TodoStyled, null,
            React.createElement(TodoNumber, null, i + 1),
            ' ',
            (() => {
                var _a, _b, _c;
                switch (todo.type) {
                    case TodoType.AddVoucher: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.economyVoucherTypeLabels[todo.details.voucherTypeId]),
                            todo.details.amount
                                ? ` ${todo.details.currency} ${formatNumber(todo.details.amount)}`
                                : '',
                            todo.details.maxTotalAmount ? (React.createElement("span", { style: { fontSize: '0.85em' } },
                                ' ',
                                '<',
                                " ",
                                formatNumber(todo.details.maxTotalAmount))) : ('')));
                    }
                    case TodoType.AddBillingVoucher: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.economyVoucherTypeLabels[todo.details.voucherTypeId]),
                            todo.details.amount
                                ? ` ${todo.details.currency} ${formatNumber(todo.details.amount)}`
                                : '',
                            ' ',
                            todo.details.text));
                    }
                    case TodoType.RemoveVoucher: {
                        return (React.createElement(React.Fragment, null,
                            lang.workflows.removeVouchers,
                            ' ',
                            React.createElement("b", null, lang.pluralEconomyVoucherTypeLabels[todo.details.voucherTypeId])));
                    }
                    case TodoType.CreateInteraction: {
                        const template = todo.details.templateId && templatesMap[todo.details.templateId];
                        return (React.createElement(React.Fragment, null,
                            editTemplateId && (React.createElement(Modal, { header: ' ', onToggle: () => setEditTemplateId(null), maxWidth: "1400px" },
                                React.createElement(TemplateEditorTabSet, { templateId: editTemplateId, preventDelete: true }))),
                            React.createElement("b", null,
                                lang.interactionTypes[todo.details.type],
                                ' ',
                                Site.Caseworker === site &&
                                    (props.onChange ? (React.createElement(Edit, { style: {
                                            cursor: 'pointer',
                                        }, onClick: () => setEditTemplateId(todo.details.templateId) })) : (React.createElement(ExternalLink, { style: {
                                            cursor: 'pointer',
                                        }, onClick: () => c.redirect.redirect(`/settings/template-editor/${todo.details.templateId}`) })))),
                            ' ',
                            template && template.name,
                            ' ',
                            React.createElement("span", null,
                                todo.details.includeVoucherAttachments && React.createElement(File, null),
                                (todo.details.attachments || []).map((a) => {
                                    const template = a.templateId && templatesMap[a.templateId];
                                    return (React.createElement(React.Fragment, { key: a.templateId },
                                        React.createElement("span", { title: template
                                                ? `${template.name} ${template.fileName
                                                    ? ` (${template.fileName})`
                                                    : ''}`
                                                : '' },
                                            React.createElement(File, null))));
                                }))));
                    }
                    case TodoType.CreateCreditorInteraction: {
                        const creditorTemplate = todo.details.templateId && templatesMap[todo.details.templateId];
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null,
                                lang.interactionTypes[todo.details.type],
                                ' ',
                                lang.workflows.creditorInteraction,
                                ' ',
                                Site.Caseworker === site && (React.createElement(ExternalLink, { style: {
                                        cursor: 'pointer',
                                    }, onClick: () => c.redirect.redirect(`/settings/template-editor/${todo.details.templateId}`) }))),
                            ' ',
                            creditorTemplate && creditorTemplate.name,
                            ' ',
                            React.createElement("span", null,
                                todo.details.includeVoucherAttachments && React.createElement(File, null),
                                (todo.details.attachments || []).map((a) => {
                                    const template = a.templateId && templatesMap[a.templateId];
                                    return (React.createElement(React.Fragment, { key: a.templateId },
                                        React.createElement("span", { title: template
                                                ? `${template.name} ${template.fileName
                                                    ? ` (${template.fileName})`
                                                    : ''}`
                                                : '' },
                                            React.createElement(File, null))));
                                }))));
                    }
                    case TodoType.SetCollector: {
                        return (React.createElement(React.Fragment, null, todo.details.useCreditor ? (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            lang.workflows.creditorCollector,
                            ' ',
                            todo.details.useCreditor)) : (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            collector && collector.name,
                            " ",
                            todo.details.useCreditor))));
                    }
                    case TodoType.SetCaseGroup: {
                        const caseGroup = todo.details.caseGroupId &&
                            (caseGroups === null || caseGroups === void 0 ? void 0 : caseGroups.find((cg) => cg.id === todo.details.caseGroupId));
                        return React.createElement(React.Fragment, null, caseGroup && caseGroup.label);
                    }
                    case TodoType.ConnectToDebtCollector: {
                        const connection = connectionLookup[todo.details.connectionId];
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            React.createElement("div", { style: {
                                    background: '#ffffff',
                                    padding: '10px',
                                    borderRadius: '3px',
                                    margin: '5px -7px -2px 0',
                                } },
                                React.createElement("div", null, (connection === null || connection === void 0 ? void 0 : connection.product.logo) != null ? (React.createElement(React.Fragment, null,
                                    React.createElement("img", { src: toLink(connection.product.logo), style: {
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '48px',
                                            marginBottom: '0.5rem',
                                        } }),
                                    React.createElement("p", { style: { margin: 0 } }, connection.product.name))) : null))));
                    }
                    case TodoType.ChangeCaseStatus: {
                        const endReason = todo.details.endReasonId
                            ? endReasonLookup[todo.details.endReasonId]
                            : null;
                        return (React.createElement("span", { title: `${lang.cases[todo.details.status]} - ${endReason
                                ? ((_a = lang.endReason[endReason.handle]) !== null && _a !== void 0 ? _a : endReason.label)
                                : ''}` },
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            lang.cases[todo.details.status],
                            " -",
                            ' ',
                            endReason
                                ? ((_b = lang.endReason[endReason.handle]) !== null && _b !== void 0 ? _b : endReason.label)
                                : ''));
                    }
                    case TodoType.SetCaseTag: {
                        return (React.createElement("b", null,
                            lang.workflowTodoTypes[todo.type],
                            " - ",
                            todo.details.tag));
                    }
                    case TodoType.SwitchWorkflow: {
                        return (React.createElement("span", { onClick: () => {
                                c.redirect.redirect(`/workflows/${todo.details.toWorkflowId}`);
                            }, style: { cursor: 'pointer' } },
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            " -",
                            ' ',
                            React.createElement("span", { style: { textDecoration: 'underline' } }, ((_c = workflowsLookup[todo.details.toWorkflowId]) === null || _c === void 0 ? void 0 : _c.title) ||
                                todo.details.toWorkflowId)));
                    }
                    case TodoType.SwitchBackToPreviousWorkflow: {
                        return React.createElement("b", null, lang.workflowTodoTypes[todo.type]);
                    }
                    case TodoType.MergeCase: {
                        return (React.createElement("b", { title: (todo.details.mergeWithRelatedCases || [])
                                .map((relationType) => lang.customerCaseRelationType[relationType])
                                .join(', ') },
                            lang.workflowTodoTypes[todo.type],
                            ' ',
                            React.createElement(Button, { theme: "text", onClick: () => {
                                    setShowTodoDetails(true);
                                } },
                                React.createElement(Info, null))));
                    }
                    case TodoType.CreateProperties: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            React.createElement("span", null, todo.details.propertyRef === 'case-voucher'
                                ? lang.properties.caseVouchers
                                : lang.properties.cases)));
                    }
                    case TodoType.Bespoke: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, todo.details.name)));
                    }
                    default: {
                        return React.createElement("b", null, lang.workflowTodoTypes[todo.type]);
                    }
                }
            })(),
            props.onChange && (React.createElement(Trash2, { style: {
                    color: THEME.COLOR_ACCENT,
                    cursor: 'pointer',
                }, onClick: () => {
                    if (props.onChange) {
                        props.onChange(props.procedure &&
                            props.procedure.filter((_, index) => index !== i));
                    }
                } }))),
        showTodoDetails && (React.createElement(Modal, { onToggle: () => {
                setShowTodoDetails(false);
            }, header: lang.workflowTodoTypes[todo.type] },
            React.createElement(TodoDetails, { todoDetails: todo.details, keyLangMap: Object.assign(Object.assign({}, lang.caseMergeRules), { mergeWithRelatedCases: lang.workflows.mergeWithRelatedCasesOfType, caseMergeRules: lang.navigation.settingsCaseMergeRules, caseStatus: lang.workflows.casesInTask }), valueLangMap: Object.assign(Object.assign(Object.assign(Object.assign({}, lang.cases.caseStatus), Object.fromEntries(Object.entries(updateTypesMap).map(([k, v]) => [k, v.label]))), lang.updateTypes), lang.customerCaseRelationType), style: { paddingLeft: 0 } })))));
};
const TodoDetails = (props) => {
    return (React.createElement(List, { style: props.style }, Object.entries(props.todoDetails).map(([detailsKey, detailsValue]) => {
        return (React.createElement(TodoDetailItem, { detailsKey: detailsKey, detailsValue: detailsValue, keyLangMap: props.keyLangMap, valueLangMap: props.valueLangMap }));
    })));
};
const TodoDetailItem = (props) => {
    const hasChildren = typeof props.detailsValue === 'object' && !!props.detailsValue;
    const isKeyANumber = !isNaN(Number(props.detailsKey));
    return (React.createElement(React.Fragment, { key: props.detailsKey },
        React.createElement("li", null,
            React.createElement("b", null, isKeyANumber
                ? ''
                : (props.keyLangMap[props.detailsKey] || props.detailsKey) + ': '),
            !hasChildren &&
                (() => {
                    if (props.detailsValue in props.valueLangMap) {
                        return props.valueLangMap[props.detailsValue];
                    }
                    if (typeof props.detailsValue === 'boolean' ||
                        !props.detailsValue) {
                        return (React.createElement(Checkbox, { disabled: true, checked: props.detailsValue || props.detailsValue === null, size: Size.Small }));
                    }
                    return props.detailsValue || '';
                })()),
        hasChildren && (React.createElement(TodoDetails, { todoDetails: props.detailsValue, keyLangMap: props.keyLangMap, valueLangMap: props.valueLangMap }))));
};
const List = styled.ul `
  li {
    background-color: #e0e0e0;
    list-style: none;
    padding: 0.5em;
    margin: 0.2em 0;
    border-radius: 3px;
  }

  ul li {
    background-color: #f0f0f0;
  }

  ul ul li {
    background-color: #f9f9f9;
  }

  ul {
    padding-left: 2em;
  }
`;
//# sourceMappingURL=Procedure.js.map